import { useEffect, useState } from 'react';
import { diceMultiplier, JACKPOT_ROLL, TARGETS, winChance, type DiceState } from '../../engine/games/dice';
import { money } from '../../format';
import { Burst, Controls, secondChanceFrame, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type TableProps } from './Frame';

/** The number races this long before it lands. */
const ROLL_MS = 800;
const LOST_MS = 900;
const BANNER_MS = 1100;
const END_HOLD_MS = 250;

const fmt = (hundredths: number) => (hundredths / 100).toFixed(2);

export function DiceTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = run.tables.dice?.state as DiceState | undefined;
  const anim = snapshot.anim;
  const luck = secondChanceFrame(snapshot);
  const lostRoll = luck ? Math.round(Number(luck.match(/Rolled (\d+\.\d+)/)?.[1] ?? NaN) * 100) : NaN;
  const hasLost = Number.isFinite(lostRoll);
  const firstEnd = hasLost ? ROLL_MS + LOST_MS + BANNER_MS : 0;
  const elapsed = useAnimClock(snapshot, actions, firstEnd + ROLL_MS + END_HOLD_MS);
  const [burst, fading, setBurst] = useBurst();
  useLuckMoment(snapshot, elapsed, hasLost ? ROLL_MS + LOST_MS : null, () => ({ what: 'A losing roll is rolled again.', saved: true }), setBurst);

  // While the counter races, show a fresh random number every frame.
  const [flicker, setFlicker] = useState(0);
  useEffect(() => {
    if (!anim) return;
    const t = setInterval(() => setFlicker(Math.floor(Math.random() * 10000)), 55);
    return () => clearInterval(t);
  }, [anim]);

  const target = state?.target ?? 50;
  const dir = state?.dir ?? 'over';
  let shown: number | null = state?.roll ?? null;
  let racing = false;
  if (anim && state?.roll !== null && state?.roll !== undefined) {
    if (hasLost && elapsed < firstEnd) {
      racing = elapsed < ROLL_MS;
      shown = racing ? flicker : lostRoll;
    } else {
      racing = elapsed - firstEnd < ROLL_MS;
      shown = racing ? flicker : state.roll;
    }
  }
  const cut = target * 100;
  const wins = (r: number) => (dir === 'over' ? r > cut : r < cut);

  // Target and direction go through the engine's pickers.
  const setTarget = (t: number) => {
    if (!state || anim) return;
    actions.tableOption('target');
    actions.tableOption(`t:${t}`);
  };
  const setDir = (d: 'over' | 'under') => {
    if (!state || anim || d === dir) return;
    actions.tableOption('dir');
  };
  const ti = TARGETS.indexOf(target);

  let result: string | undefined;
  let tone: 'win' | 'loss' | '' = '';
  if (state && state.roll !== null && !anim) {
    const net = state.returned - state.lastStake;
    result = `Last roll: ${fmt(state.roll)} · ${net > 0 ? `${state.lucky ? '★ ' : ''}+${money(net)}` : `−${money(state.lastStake)}`}${state.roll === JACKPOT_ROLL ? ' · 77.77 jackpot!' : ''}`;
    tone = net > 0 ? 'win' : 'loss';
  }

  return (
    <>
      <Stage className="dice-roll-stage" onClick={anim ? actions.skip : undefined}>
        <Burst burst={burst} fading={fading} pending={false} />
        <div className="arcade-screen">
          <div className="arcade-title">HI · LO · ROLL</div>
          <div className={`roll-readout${racing ? ' racing' : shown !== null ? (wins(shown) ? ' won' : ' lost') : ''}`}>{shown === null ? '––.––' : fmt(shown)}</div>
          <div className="roll-track" aria-label={`Win if the roll is ${dir} ${target}`}>
            <div className="roll-zone" style={dir === 'over' ? { left: `${target}%`, right: 0 } : { left: 0, right: `${100 - target}%` }} />
            <div className="roll-target" style={{ left: `${target}%` }}><span>{target}</span></div>
            {shown !== null && !racing && <div className={`roll-marker${wins(shown) ? ' won' : ' lost'}`} style={{ left: `${shown / 100}%` }} />}
            <div className="roll-ends"><span>0</span><span>100</span></div>
          </div>
        </div>
        <div className="dice-picker">
          <div className="picker-row">
            <span className="picker-label">Win if</span>
            <div className="picker-counts">
              <button className={`pick${dir === 'over' ? ' on' : ''}`} onClick={() => setDir('over')} disabled={!!anim}>Over</button>
              <button className={`pick${dir === 'under' ? ' on' : ''}`} onClick={() => setDir('under')} disabled={!!anim}>Under</button>
            </div>
          </div>
          <div className="picker-row">
            <span className="picker-label">Target</span>
            <div className="bet-control target-step">
              <button className="step" onClick={() => setTarget(TARGETS[ti - 1]!)} disabled={!!anim || ti <= 0} aria-label="Lower target">−</button>
              <div className="chip target-chip">{target}</div>
              <button className="step" onClick={() => setTarget(TARGETS[ti + 1]!)} disabled={!!anim || ti >= TARGETS.length - 1} aria-label="Raise target">+</button>
            </div>
          </div>
          <div className="picker-odds">
            <span>{(winChance(target, dir) * 100).toFixed(2)}% to win</span>
            <span>pays ×{diceMultiplier(target, dir).toFixed(2)}</span>
          </div>
        </div>
        <StatusLine snapshot={snapshot} result={result} tone={tone} />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} hide={['target', 'dir']} onAct={() => setBurst(null)} />
    </>
  );
}
